import { ReactComponent as FathomLogo } from '@/assets/fathom.svg'
import { GoogleAuthButton } from '@/web-app/views/users/components/google-auth-button'
import { MicrosoftAuthButton } from '@/web-app/views/users/components/microsoft-auth-button'
import { SSOAuthButton } from '@/web-app/views/users/components/sso-auth-button'
import { SocialProofFooter } from '@/web-app/views/users/components/social-proof-footer'
import { Testimonial } from '@/web-app/views/users/components/testimonial'
import { usePageProps } from '@/shared/hooks/use-page-props'
import { useQueryUrl } from '@/shared/hooks/use-query-url'
import { useAllowlistSearchParams } from '@/web-app/hooks/use-allowlist-search-params'
import { buildUrlWithParams } from '@/web-app/utils/build-url-with-params'
import { addAnalyticsToUrl } from '@/web-app/utils/analytics'

export interface ISignInData {
  googleUrl: string
  microsoftUrl: string
  ssoSignInUrl: string
  fromGoogle: boolean
  fromMicrosoft: boolean
  showMissingScopesError: boolean
}

export default function SignIn() {
  const { pageProps } = usePageProps<{ signInDataUrl: string }>()

  const { allowedParams } = useAllowlistSearchParams({
    allowedKeys: ['invite', 'invite_source', 'ps_partner_key', 'questionnaire', 'share_token'],
  })

  const signInDataUrlWithParams =
    pageProps?.signInDataUrl && Object.keys(allowedParams).length > 0
      ? buildUrlWithParams(pageProps.signInDataUrl, allowedParams as Record<string, string>)
      : pageProps?.signInDataUrl

  const { data: signInData, isLoading: isLoadingSignInData } = useQueryUrl<ISignInData>(signInDataUrlWithParams)
  const { googleUrl, microsoftUrl, ssoSignInUrl, fromGoogle, fromMicrosoft, showMissingScopesError } = signInData ?? {}

  if (isLoadingSignInData) {
    return <div className='fv:flex fv:flex-col fv:min-h-screen fv:bg-black fv:overflow-auto' />
  }

  return (
    <div className='fv:flex fv:flex-col fv:min-h-screen fv:bg-black fv:overflow-auto'>
      <div className='fv:flex fv:justify-center fv:pt-11 fv:pb-22'>
        <a href={addAnalyticsToUrl('https://fathom.ai')}>
          <FathomLogo className='fv:w-44 fv:text-off-white' />
        </a>
      </div>

      {/* Main content area */}
      <div className='fv:flex fv:flex-col fv:lg:flex-row fv:justify-center fv:lg:justify-between fv:items-center fv:flex-1 fv:w-full fv:max-w-screen-xl fv:mx-auto fv:px-4 fv:lg:px-25'>
        {/* Card */}
        <div className='fv:relative fv:flex fv:flex-col fv:items-center fv:w-full fv:lg:w-auto fv:max-w-lg fv:h-fit fv:rounded-3xl fv:border fv:border-off-white/25 fv:bg-neutral-900/80 fv:backdrop-blur-sm fv:px-5 fv:pt-14 fv:pb-8'>
          {/* Emoji + Title */}
          <h1 className='fv:text-3xl fv:font-bold fv:text-off-white fv:text-center'>Sign in to Fathom</h1>

          {/* Auth buttons */}
          <div className='fv:mt-8 fv:flex fv:flex-col fv:gap-4'>
            <GoogleAuthButton
              authUrl={googleUrl}
              label='Continue with Google'
              fromGoogle={fromGoogle}
              showMissingScopesError={showMissingScopesError}
            />
            <MicrosoftAuthButton
              authUrl={microsoftUrl}
              label='Continue with Microsoft'
              fromMicrosoft={fromMicrosoft}
              showMissingScopesError={showMissingScopesError}
            />
            <SSOAuthButton url={ssoSignInUrl} label='Continue with SSO' />
          </div>

          {/* Sign up link */}
          <div className='fv:mt-10 fv:text-sm fv:text-off-white fv:text-center'>
            <p>
              New to Fathom?{' '}
              <a href='/users/sign_up' className='fv:text-accent-fathom fv:underline'>
                Sign up
              </a>
            </p>
          </div>

          {/* Terms */}
          <p className='fv:mt-auto fv:pt-10 fv:text-xs fv:text-white/40 fv:text-center'>
            By using Fathom, you agree to the{' '}
            <a href='/terms' className='fv:underline'>
              Terms of Service
            </a>{' '}
            and{' '}
            <a href='/privacy' className='fv:underline'>
              Privacy Policy
            </a>
            .
          </p>
        </div>

        {/* Testimonial */}
        <div className='fv:flex fv:flex-1 fv:items-center fv:justify-center fv:pt-32 fv:pb-14 fv:lg:py-0 fv:px-14'>
          <Testimonial />
        </div>
      </div>

      {/* Bottom social proof bar */}
      <div className='fv:py-11'>
        <SocialProofFooter />
      </div>
    </div>
  )
}
